/**
 * Facebook Conversion Tracking Service
 * Handles tracking of Facebook Pixel events for conversion optimization
 */

import { consentAllows } from '@common/services/consent/gate';
import { isMetaEventAllowed, sendMetaEvent, type MetaEventParams } from '@common/services/meta-pixel-dispatch';

// Extend Window interface to include Facebook Pixel
declare global {
  interface Window {
    fbq?: (command: string, eventName: string, params?: Record<string, any>, options?: { eventID?: string }) => void;
  }
}

/**
 * Standard Facebook Pixel events
 */
export enum FacebookEvent {
  ViewContent = 'ViewContent',
  CompleteRegistration = 'CompleteRegistration',
  Lead = 'Lead',
  InitiateCheckout = 'InitiateCheckout',
  Purchase = 'Purchase',
  StartTrial = 'StartTrial',
}

/**
 * Track a Facebook Pixel event with optional parameters
 * @param eventName - The Facebook event to track
 * @param params - Optional parameters to include with the event
 * @param eventId - Dedup key shared with the server-side (CAPI) copy of the same event
 */
export function trackFacebookEvent(
  eventName: FacebookEvent,
  params?: MetaEventParams,
  eventId?: string
): void {
  try {
    // Meta Pixel is marketing: no event leaves the page without marketing consent (spec section 2).
    if (!consentAllows('marketing')) return;
    if (!isMetaEventAllowed(eventName)) return;

    // Check if Facebook Pixel is loaded
    if (typeof window === 'undefined' || !window.fbq) {
      console.warn('Facebook Pixel (fbq) is not available');
      return;
    }

    // sendMetaEvent applies the standard-event and parameter allowlists and the eventID option.
    const fbq = window.fbq;
    if (!fbq) return;
    sendMetaEvent(fbq, eventName, params ?? {}, eventId);

    console.log(`Facebook Pixel event tracked: ${eventName}`);
  } catch (error) {
    console.warn('Error tracking Facebook event:', error);
  }
}

/**
 * Deterministic Meta event_id for a Purchase, derived from the Chargebee subscription id.
 * The backend CAPI copy builds the same string, so Meta deduplicates the two (#855).
 */
export function purchaseEventId(subscriptionId: string): string {
  return `purchase_${subscriptionId}`;
}

/** Meta event_id for a trial start; the backend CAPI copy builds the same string (`metaStartTrialEventId`). */
export function trialEventId(subscriptionId: string): string {
  return `trial_${subscriptionId}`;
}

/** Browser StartTrial, deduplicated against the server copy. No-op without the subscription id. */
export function trackStartTrial(subscriptionId: string | undefined): void {
  if (!subscriptionId) return;
  trackFacebookEvent(FacebookEvent.StartTrial, undefined, trialEventId(subscriptionId));
}

/**
 * Chargebee's `plan_unit_price` is in minor units (2950 = 29.50 USD); the backend CAPI copy divides
 * by 100 too, so the pixel must report the same major-unit value or Meta sees 100x on the first copy.
 * Note this is the list price: it ignores coupons and tax, unlike the CAPI amount paid.
 */
export function purchaseValueFromMinorUnits(unitPriceMinor: number, quantity: number): number {
  return Math.round(unitPriceMinor * quantity) / 100;
}

/**
 * Track a Facebook Purchase event
 * @param params - Purchase parameters including transaction_id (the subscription id), value, currency, and planId
 */
export function trackFacebookPurchase(params: {
  transaction_id: string;
  value: number;
  currency: string;
  planId?: string;
}): void {
  const purchaseParams: Record<string, unknown> = {
    value: params.value,
    currency: params.currency,
    content_ids: params.planId ? [params.planId] : undefined,
    content_name: params.planId,
  };

  // Remove undefined values
  Object.keys(purchaseParams).forEach(key => {
    if (purchaseParams[key] === undefined) {
      delete purchaseParams[key];
    }
  });

  trackFacebookEvent(FacebookEvent.Purchase, purchaseParams, purchaseEventId(params.transaction_id));
}

/**
 * Track a signup/registration event
 */
export function trackSignup(eventId?: string): void {
  trackFacebookEvent(FacebookEvent.CompleteRegistration, undefined, eventId);
}

/**
 * Track an extension install event as a Lead
 */
export function trackExtensionInstall(): void {
  trackFacebookEvent(FacebookEvent.Lead, {
    content_name: 'Extension Install',
  });
}
